// Set/rep table for the calendar sidebar "phone" preview.
// Mirrors the workouts module's WorkoutEntryTable visually (set/round column, headers,
// value inputs, rest-timer row, complete button). Inputs are readOnly so they keep their
// normal look; interaction is blocked by the transparent overlay in CalendarExerciseDetail.
import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { BoxShadow } from '../../../../shared/GlobalStyles';

const RepsBox = styled('div')`
  display: flex;
  margin-bottom: 25px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${BoxShadow};
  flex-direction: column;
  align-items: center;
  width: ${(props) => `${props.width * 75}px`};
  min-width: 100%;
  padding-bottom: 30px;
  padding-right: 15px;
  padding-left: 15px;
`;

const StandardBox = styled('div')`
  display: flex;
  justify-content: center;
  flex: 1;
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: 11px;
  align-items: center;
`;

const SetNameBox = styled('div')`
  display: flex;
  justify-content: center;
  flex: 1;
  font-family: 'Nunito Sans';
  font-weight: 600;
  font-size: 9px;
  align-items: center;
`;

const StyledInput = styled('input')`
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 13px;
  border: 1px solid #e8e8e8;
  text-align: center;
  width: 90%;
  border-radius: 8px;
  height: 38px;
  background: ${({ theme }) => theme.colors.surface};
`;

const StandardRow = styled('div')`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 15px;
`;

const CompleteButton = styled('button')`
  width: 140px;
  height: 37px;
  border-radius: 40px;
  box-shadow: ${BoxShadow};
  border: ${(props) => (props.isWorkoutComplete ? '1px solid #00b371;' : `1px solid ${props.theme.colors.borderLight}`)};
  margin-top: 20px;
  background: ${(props) => (props.isWorkoutComplete ? '#00b371;' : props.theme.colors.surface)};
`;

const RestTimerButton = styled('button')`
  width: 100%;
  border-radius: 10px;
  margin-top: 15px;
  background: #3fbfe1;
  border: none;
  padding: 2px;
  height: 27px;
`;

const InnerCompleteButton = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CompleteButtonText = styled('div')`
  font-size: 12px;
  margin-left: 10px;
  font-weight: 600;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.isWorkoutComplete ? 'white' : props.theme.colors.text)};
`;

const RestTimerButtonText = styled('div')`
  font-size: 9px;
  margin-left: 10px;
  font-weight: 800;
  font-family: 'Nunito Sans';
  color: white;
`;

const SetNameText = styled('div')`
  width: 80%;
  display: flex;
  justify-content: center;
  white-space: normal;
  text-align: center;
`;

const SetNumberAndPercentage = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-family: 'Nunito Sans';
`;

const Percentage = styled('div')`
  display: flex;
  font-family: 'Nunito Sans';
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 9px;
`;

const CoachCompletionOnlyText = styled('div')`
  white-space: normal;
  font-family: 'Nunito Sans';
  text-align: center;
  margin-top: 25px;
  font-size: 10px;
  font-weight: 500;
`;

const ButtonContainer = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const renderSetRestTimer = (timerLengthInSeconds) => (
  <RestTimerButton type='button'>
    <InnerCompleteButton>
      <IcomoonReact iconSet={iconSet} size={15} icon='clock' color='white' />
      <RestTimerButtonText>
        {`REST TIMER (${timerLengthInSeconds} SECS)`}
      </RestTimerButtonText>
    </InnerCompleteButton>
  </RestTimerButton>
);

const CalendarEntryTable = ({ workoutObject }) => {
  const { colors } = useTheme();
  if (!workoutObject?.tableHeaders) {
    return null;
  }

  return (
    <RepsBox width={workoutObject.tableHeaders.length + 1}>
      {workoutObject.tableHeaders.length ? (
        <StandardRow>
          <SetNameBox>{workoutObject?.type === 'C' ? 'ROUND' : 'SET'}</SetNameBox>
          {workoutObject.tableHeaders.map((header) => (
            <SetNameBox key={header.valueDescription}>
              <SetNameText>{header?.valueDescription.toUpperCase()}</SetNameText>
            </SetNameBox>
          ))}
        </StandardRow>
      ) : null}
      {workoutObject.tableData.map((tableRow) => (
        <React.Fragment key={tableRow.setId}>
          <StandardRow>
            <StandardBox>
              <SetNumberAndPercentage>
                {tableRow.setId}
                {tableRow.percentage ? (
                  <Percentage>{`(${tableRow.percentage}%)`}</Percentage>
                ) : null}
              </SetNumberAndPercentage>
            </StandardBox>
            {tableRow.values.map((rowItem, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <StandardBox key={`${tableRow.setId}-${rowItem.valueName}-${idx}`}>
                {rowItem.readOnly ? (
                  rowItem.value
                ) : (
                  <StyledInput
                    readOnly
                    value={rowItem.value || rowItem.value === 0 ? rowItem.value : ''}
                    placeholder={
                      !rowItem.value && (rowItem.placeholder || rowItem.placeholder === 0)
                        ? rowItem.placeholder
                        : ''
                    }
                    type={rowItem?.valueType === 'STRING' ? 'text' : 'number'}
                  />
                )}
              </StandardBox>
            ))}
          </StandardRow>
          {tableRow.restInSeconds ? renderSetRestTimer(tableRow.restInSeconds) : null}
        </React.Fragment>
      ))}
      {workoutObject.completionCheckbox ? (
        <ButtonContainer>
          <CompleteButton type='button' isWorkoutComplete={workoutObject.fullyCompleted}>
            <InnerCompleteButton>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='checkmark'
                color={!workoutObject.fullyCompleted ? colors.text : 'white'}
              />
              <CompleteButtonText isWorkoutComplete={workoutObject.fullyCompleted}>
                Complete
              </CompleteButtonText>
            </InnerCompleteButton>
          </CompleteButton>
        </ButtonContainer>
      ) : null}
      {workoutObject?.coachCompletionOnly ? (
        <CoachCompletionOnlyText>
          A coach must complete this workout item
        </CoachCompletionOnlyText>
      ) : null}
    </RepsBox>
  );
};

CalendarEntryTable.propTypes = {
  workoutObject: PropTypes.instanceOf(Object).isRequired,
};

export default CalendarEntryTable;
